import React, { Component, ErrorInfo, ReactNode } from 'react';
import { Box, Button, VStack, Text, Heading, useColorModeValue as getColorModeValue } from '@chakra-ui/react';
import { FiAlertCircle, FiRefreshCw, FiHome } from 'react-icons/fi';
import IconWrapper from './IconWrapper';

interface Props {
  children: ReactNode;
  fallback?: ReactNode;
}

interface State {
  hasError: boolean;
  error: Error | null;
  errorInfo: ErrorInfo | null;
}

// Wrapper component to use hooks
const ErrorDisplay: React.FC<{ error: Error | null; errorInfo: ErrorInfo | null; onReset: () => void }> = ({ error, errorInfo, onReset }) => {
  const bgLight = getColorModeValue('gray.50', 'gray.900');
  const bgCard = getColorModeValue('white', 'gray.800');
  const textColor = getColorModeValue('gray.900', 'white');
  const textSecondary = getColorModeValue('gray.600', 'gray.400');
  const errorBg = getColorModeValue('red.50', 'red.900');
  const errorText = getColorModeValue('gray.700', 'gray.300');

  return (
    <Box
      minH="100vh"
      display="flex"
      alignItems="center"
      justifyContent="center"
      bg={bgLight}
      p={4}
    >
      <VStack
        maxW="600px"
        spacing={6}
        textAlign="center"
        bg={bgCard}
        p={8}
        borderRadius="lg"
        boxShadow="lg"
      >
        <IconWrapper icon={FiAlertCircle} size={64} color="red.500" />
        <Heading size="lg" color={textColor}>
          Something went wrong
        </Heading>
        <Text color={textSecondary}>
          We're sorry, but something unexpected happened. Our team has been notified.
        </Text>
        {process.env.NODE_ENV === 'development' && error && (
          <Box
            w="100%"
            p={4}
            bg={errorBg}
            borderRadius="md"
            textAlign="left"
            fontSize="sm"
            fontFamily="mono"
            overflow="auto"
            maxH="200px"
          >
            <Text fontWeight="bold" mb={2}>
              Error: {error.toString()}
            </Text>
            {errorInfo && (
              <Text fontSize="xs" color={errorText}>
                {errorInfo.componentStack}
              </Text>
            )}
          </Box>
        )}
        <VStack spacing={3} w="100%">
          <Button
            leftIcon={<IconWrapper icon={FiRefreshCw} size={16} />}
            colorScheme="purple"
            onClick={onReset}
            w="100%"
          >
            Try Again
          </Button>
          <Button
            leftIcon={<IconWrapper icon={FiHome} size={16} />}
            variant="outline"
            onClick={() => (window.location.href = '/')}
            w="100%"
          >
            Go Home
          </Button>
        </VStack>
      </VStack>
    </Box>
  );
};

class ErrorBoundary extends Component<Props, State> {
  constructor(props: Props) {
    super(props);
    this.state = {
      hasError: false,
      error: null,
      errorInfo: null,
    };
  }

  static getDerivedStateFromError(error: Error): State {
    return {
      hasError: true,
      error,
      errorInfo: null,
    };
  }

  componentDidCatch(error: Error, errorInfo: ErrorInfo): void {
    this.setState({
      error,
      errorInfo,
    });

    // Log error to console
    console.error('ErrorBoundary caught an error:', error, errorInfo);
  }

  handleReset = (): void => {
    this.setState({
      hasError: false,
      error: null,
      errorInfo: null,
    });
  };

  render(): ReactNode {
    if (this.state.hasError) {
      if (this.props.fallback) {
        return this.props.fallback;
      }

      return (
        <ErrorDisplay
          error={this.state.error}
          errorInfo={this.state.errorInfo}
          onReset={this.handleReset}
        />
      );
    }

    return this.props.children;
  }
}

export default ErrorBoundary;

